import {
  JaNej,
  LawyerOrLawFirmStatus,
  ParticipantType,
  WithdrawalReason,
} from "../model/registrationEnums";
import type { ParticipantEligibilityValues } from "../model/registrationTypes";

export const INELIGIBLE_PARTICIPANT_MESSAGE =
  "Denne deltager kan ikke tilføjes.";
export const MAX_NON_LAWYER_OWNER_SHARE = 9.99;
const MAX_LAWYER_OWNER_SHARE = 100;

export type ParticipantEligibilityRow = Pick<
  ParticipantEligibilityValues,
  | "isLawyerOrLawFirm"
  | "passedPartnerExam"
  | "ownerShare"
  | "leavesOtherOnEntry"
>;

export type ChangePartyEligibilityRow = Pick<
  ParticipantEligibilityValues,
  | "isLawyerOrLawFirm"
  | "passedPartnerExam"
  | "ownerShare"
  | "leavesOtherOnEntry"
> & {
  direction: string;
  voteShare: string;
  otherCompanyName: string;
  participantType: string;
  withdrawalReason: string;
  continuedCompanyName: string;
  continuedCompanyCvr: string;
};

export const requiresPartnerExamField = (isLawyerOrLawFirm: string): boolean =>
  isLawyerOrLawFirm === LawyerOrLawFirmStatus.IkkeAdvokat;

export const isIneligibleLawFirm = (isLawyerOrLawFirm: string): boolean =>
  isLawyerOrLawFirm === LawyerOrLawFirmStatus.IkkeAdvokatselskab;

export const isIneligiblePartnerExam = (passedPartnerExam: string): boolean =>
  passedPartnerExam === JaNej.Nej;

export const isEligibleLawFirmStatus = (isLawyerOrLawFirm: string): boolean =>
  !isIneligibleLawFirm(isLawyerOrLawFirm);

export const isEligiblePartnerExamStatus = (
  passedPartnerExam: string,
): boolean => !isIneligiblePartnerExam(passedPartnerExam);

/** Participant can be added and follow-up questions apply. */
export const isEligibleParticipant = (
  isLawyerOrLawFirm: string,
  passedPartnerExam: string,
): boolean => {
  if (!isLawyerOrLawFirm || isIneligibleLawFirm(isLawyerOrLawFirm)) {
    return false;
  }

  if (requiresPartnerExamField(isLawyerOrLawFirm)) {
    return passedPartnerExam === JaNej.Ja;
  }

  return isLawyerOrLawFirm === LawyerOrLawFirmStatus.Ja;
};

/** Show owner + vote share fields for eligible participants. */
export const canEnterCreationShareFields = isEligibleParticipant;

/** Show and require leavesOtherOnEntry for eligible participants. */
export const requiresLeavesOtherOnEntry = isEligibleParticipant;

/** Show owner share field for non-lawyers (e.g. interessentskab). */
export const canEnterChangeOwnerShare = (
  isLawyerOrLawFirm: string,
  passedPartnerExam: string,
): boolean => requiresNonLawyerOwnerShare(isLawyerOrLawFirm, passedPartnerExam);

export const requiresNonLawyerOwnerShare = (
  isLawyerOrLawFirm: string,
  passedPartnerExam: string,
): boolean =>
  requiresPartnerExamField(isLawyerOrLawFirm) && passedPartnerExam === JaNej.Ja;

export const isValidNonLawyerOwnerShare = (value: string): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.replace(",", ".");
  const share = Number.parseFloat(normalized);

  return (
    !Number.isNaN(share) && share > 0 && share <= MAX_NON_LAWYER_OWNER_SHARE
  );
};

export const isValidLawyerOwnerShare = (value: string): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.replace(",", ".");
  const share = Number.parseFloat(normalized);

  return !Number.isNaN(share) && share > 0 && share <= MAX_LAWYER_OWNER_SHARE;
};

export const shouldClearPassedPartnerExam = (
  isLawyerOrLawFirm: string,
): boolean => !requiresPartnerExamField(isLawyerOrLawFirm);

export const shouldClearCreationOwnerShare = (
  isLawyerOrLawFirm: string,
  passedPartnerExam: string,
): boolean =>
  requiresPartnerExamField(isLawyerOrLawFirm) && passedPartnerExam !== JaNej.Ja;

export const shouldClearLeavesOtherOnEntry = (
  isLawyerOrLawFirm: string,
  passedPartnerExam: string,
): boolean => !isEligibleParticipant(isLawyerOrLawFirm, passedPartnerExam);

export const shouldClearChangeOwnerShare = (
  passedPartnerExam: string,
): boolean => passedPartnerExam !== JaNej.Ja;

export const syncCreationParticipantEligibility = <
  T extends ParticipantEligibilityRow,
>(
  participant: T,
): T => {
  let next = { ...participant };

  if (shouldClearPassedPartnerExam(next.isLawyerOrLawFirm)) {
    next = { ...next, passedPartnerExam: "" };
  }

  if (
    shouldClearCreationOwnerShare(
      next.isLawyerOrLawFirm,
      next.passedPartnerExam,
    )
  ) {
    next = { ...next, ownerShare: "" };
  }

  if (
    shouldClearLeavesOtherOnEntry(
      next.isLawyerOrLawFirm,
      next.passedPartnerExam,
    )
  ) {
    next = { ...next, leavesOtherOnEntry: "" };
  }

  return next;
};

export const resetLeavingPartyEligibility = <
  T extends ChangePartyEligibilityRow,
>(
  party: T,
): T => ({
  ...party,
  isLawyerOrLawFirm: LawyerOrLawFirmStatus.Ja,
  passedPartnerExam: "",
  ownerShare: "",
  voteShare: "",
  leavesOtherOnEntry: "",
  otherCompanyName: "",
});

export const clearLeavingPartyFields = <T extends ChangePartyEligibilityRow>(
  party: T,
): T => ({
  ...party,
  participantType: "",
  withdrawalReason: "",
  continuedCompanyName: "",
  continuedCompanyCvr: "",
});

export const syncLeavingPartyFields = <T extends ChangePartyEligibilityRow>(
  party: T,
): T => {
  let next = { ...party };

  if (next.participantType !== ParticipantType.Person) {
    next = {
      ...next,
      withdrawalReason: "",
      continuedCompanyName: "",
      continuedCompanyCvr: "",
    };
  } else if (next.withdrawalReason !== WithdrawalReason.ContinuesAsLawyer) {
    next = { ...next, continuedCompanyName: "", continuedCompanyCvr: "" };
  }

  return next;
};

export const syncChangeEnteringPartyEligibility = <
  T extends ChangePartyEligibilityRow,
>(
  party: T,
): T => {
  let next = { ...party };

  if (shouldClearPassedPartnerExam(next.isLawyerOrLawFirm)) {
    next = { ...next, passedPartnerExam: "" };
  } else if (shouldClearChangeOwnerShare(next.passedPartnerExam)) {
    next = { ...next, ownerShare: "" };
  }

  if (
    !canEnterChangeOwnerShare(next.isLawyerOrLawFirm, next.passedPartnerExam)
  ) {
    next = { ...next, ownerShare: "" };
  }

  if (
    shouldClearLeavesOtherOnEntry(
      next.isLawyerOrLawFirm,
      next.passedPartnerExam,
    )
  ) {
    next = { ...next, leavesOtherOnEntry: "", otherCompanyName: "" };
  } else if (next.leavesOtherOnEntry !== JaNej.Ja) {
    next = { ...next, otherCompanyName: "" };
  }

  return { ...next, voteShare: "" };
};

export const syncInteressentskabParticipantEligibility = <
  T extends ParticipantEligibilityRow & { voteShare?: string },
>(
  participant: T,
): T => {
  if (participant.isLawyerOrLawFirm !== LawyerOrLawFirmStatus.Ja) {
    return {
      ...participant,
      passedPartnerExam: "",
      ownerShare: "",
      leavesOtherOnEntry: "",
      voteShare: "",
    };
  }

  return {
    ...participant,
    passedPartnerExam: "",
    ownerShare: "",
    voteShare: "",
  };
};

export const syncChangeInteressentskabEnteringPartyEligibility = <
  T extends ChangePartyEligibilityRow,
>(
  party: T,
): T => {
  const next = syncInteressentskabParticipantEligibility(party);

  if (next.isLawyerOrLawFirm !== LawyerOrLawFirmStatus.Ja) {
    return { ...next, leavesOtherOnEntry: "", otherCompanyName: "" };
  }

  if (next.leavesOtherOnEntry !== JaNej.Ja) {
    return { ...next, otherCompanyName: "" };
  }

  return next;
};

export const syncChangeAdvokatselskabEnteringPartyEligibility = <
  T extends ChangePartyEligibilityRow,
>(
  party: T,
): T => {
  let next = { ...party };

  if (shouldClearPassedPartnerExam(next.isLawyerOrLawFirm)) {
    next = { ...next, passedPartnerExam: "" };
  } else if (
    shouldClearCreationOwnerShare(
      next.isLawyerOrLawFirm,
      next.passedPartnerExam,
    )
  ) {
    next = { ...next, ownerShare: "", voteShare: "" };
  }

  if (
    !canEnterCreationShareFields(next.isLawyerOrLawFirm, next.passedPartnerExam)
  ) {
    next = { ...next, ownerShare: "", voteShare: "" };
  }

  if (
    shouldClearLeavesOtherOnEntry(
      next.isLawyerOrLawFirm,
      next.passedPartnerExam,
    )
  ) {
    next = { ...next, leavesOtherOnEntry: "", otherCompanyName: "" };
  } else if (next.leavesOtherOnEntry !== JaNej.Ja) {
    next = { ...next, otherCompanyName: "" };
  }

  return next;
};

export const creationParticipantEligibilityChanged = (
  before: ParticipantEligibilityRow,
  after: ParticipantEligibilityRow,
): boolean =>
  before.isLawyerOrLawFirm !== after.isLawyerOrLawFirm ||
  before.passedPartnerExam !== after.passedPartnerExam ||
  before.ownerShare !== after.ownerShare ||
  before.leavesOtherOnEntry !== after.leavesOtherOnEntry;
